import styled from "@emotion/styled/macro";
import {
  Modal,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import { useCallback, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useParams } from "react-router-dom";
import { ListOffer } from "../../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
// eslint-disable-next-line import/no-restricted-paths
import { SolarContent } from "./OfferCardSolarContent";
// eslint-disable-next-line import/no-restricted-paths
import { BatteryOnlyContent } from "./OfferCardBatteryContent";
// eslint-disable-next-line import/no-restricted-paths
import { EvChargerOnlyContent } from "./OfferCardEvChargerContent";
import { maxWidth } from "components/atoms/GlobalStyles";
import { emptyOffer, deleteOffer } from "services/storeFront";
import { useAppSelector } from "store";
import {
  reloadMFE,
  useNavigateToBuild,
  useNavigateToReview,
} from "utils/useNavigateHelper";
import { financialProductDisplayText } from "constants/financialProducts";
import { useStorefrontDesignImages } from "hooks/useStorefrontDesignImages";
import { sendTrackingEvent } from "services/fullstory";
import { BUILDER_OFFER_LABEL } from "constants/newHomes";
import { isBuilderOffer } from "utils/builderOffer";

import { CopyIcon } from "components/atoms/CopyIcon";
import { RemoveIcon } from "components/atoms/RemoveIcon";

const OfferCard = ({
  offer,
  onLoading,
  setError,
}: {
  offer: ListOffer;
  onLoading: (message: string | undefined) => void;
  setError: React.Dispatch<React.SetStateAction<Error | undefined>>;
}) => {
  const isMobile = useMediaQuery({ maxWidth: maxWidth });
  const isTablet = useMediaQuery({ maxWidth: 1024 });
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState<boolean>(false);
  const { prospectId } = useParams();
  const navigateToReview = useNavigateToReview();
  const navigateToBuild = useNavigateToBuild();
  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );
  const removeOffer = useCallback(() => {
    if (!cognitoToken) {
      return;
    }
    onLoading("Removing offer...");
    setShowDeleteConfirmationModal(false);
    deleteOffer(cognitoToken, offer.id, offer.lockVersion).finally(() => {
      // Succuss or failure, reload the page to show the current state
      reloadMFE();
    });
  }, [
    cognitoToken,
    offer.id,
    offer.lockVersion,
    onLoading,
    setShowDeleteConfirmationModal,
  ]);

  const copyOffer = useCallback(() => {
    if (!cognitoToken || !prospectId || !offer.proposalId) {
      return;
    }
    onLoading("Copying offer...");
    sendTrackingEvent("New Offer Start", { source: "Copy Offer" });
    // For New Homes, force fresh Opportunity data to ensure buyer info is correct
    emptyOffer(
      cognitoToken,
      prospectId,
      isBuilderOffer(offer) ? ["janUsage"] : undefined,
      offer.proposalId
    )
      .then((result) => {
        navigateToBuild(result.id, offer.proposalId, { skipToPricing: true });
      })
      .catch((error) => {
        setError(error as Error);
        onLoading(undefined);
      });
  }, [cognitoToken, navigateToBuild, offer, onLoading, prospectId, setError]);

  const financing = useMemo(() => {
    switch (offer.financing) {
      case "Cash":
        return "Purchase";
      case "Loan":
        return "Loan";
      case "Monthly":
        return "Monthly";
      case "Prepaid":
        return "Pre-Paid";
      case "FlexMonthly":
        return financialProductDisplayText["FlexMonthly"];
    }
  }, [offer]);
  const designImages = useStorefrontDesignImages(
    offer?.designId,
    offer?.designVersion
  );
  const isBuilder = isBuilderOffer(offer);

  const OfferCardContent = () => {
    if (offer.isOnlyBattery) {
      return (
        <BatteryOnlyContent
          offer={offer}
          financing={financing}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      );
    } else if (offer.isOnlyEvCharger) {
      return (
        <EvChargerOnlyContent
          offer={offer}
          financing={financing}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      );
    } else {
      return (
        <SolarContent
          designImages={designImages}
          offer={offer}
          financing={financing}
        />
      );
    }
  };

  return (
    <OfferCardContainer $isBuilder={isBuilder} data-testid="offer-card">
      {showDeleteConfirmationModal && (
        <Modal
          testId="offer-list-page-delete-confirmation-modal"
          type="warning"
          title="Are you sure you want to remove the offer?"
          onClose={() => setShowDeleteConfirmationModal(false)}
          primaryButton={{
            text: "Delete Offer",
            onClick: removeOffer,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setShowDeleteConfirmationModal(false),
          }}
        ></Modal>
      )}
      {isBuilder && (
        <BuilderOfferLabel data-testid="builder-offer-label">
          {BUILDER_OFFER_LABEL}
        </BuilderOfferLabel>
      )}
      <OfferCardContent />
      <CardFooter data-testid="offer-card-footer">
        <CustomButton onClick={() => navigateToReview(offer.id)}>
          Review
        </CustomButton>
        {offer.proposalId && !offer.isNonSolar && (
          <CopyIcon
            aria-label="Copy offer"
            data-testid="copy-offer-btn"
            onClick={copyOffer}
          />
        )}
        <RemoveIcon onClick={() => setShowDeleteConfirmationModal(true)} />
      </CardFooter>
    </OfferCardContainer>
  );
};

const NewOfferCard = ({
  disableAddNewOffer,
  onAddNewOffer,
}: {
  disableAddNewOffer?: boolean;
  onAddNewOffer: () => void;
}) => {
  return (
    <NewOfferCardContainer
      disableAddNewOffer={disableAddNewOffer}
      data-testid={"new-offer-btn"}
      onClick={() => {
        if (disableAddNewOffer) {
          return;
        }
        onAddNewOffer();
      }}
    >
      <SvgIcon name={SvgNames.Plus} width="25px" color={tokens.BLACK} />
      <Typography size={"large"}>Add New Offer</Typography>
    </NewOfferCardContainer>
  );
};

const NewOfferCardContainer = styled.div<{
  disableAddNewOffer?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;

  border-radius: 8px;
  border: 1px dashed ${tokens.OFFWHITE_40};

  background-color: ${tokens.OFFWHITE_70};
  min-width: 320px;
  max-width: 320px;

  &:hover {
    background-color: ${tokens.OFFWHITE_80};
  }

  &:active {
    background-color: ${tokens.OFFWHITE_60};
  }

  cursor: ${({ disableAddNewOffer }) =>
    disableAddNewOffer ? "not-allowed" : "pointer"};

  opacity: ${({ disableAddNewOffer }) => (disableAddNewOffer ? 0.5 : 1)};

  & svg {
    border: 3px solid black;
    padding: 8px;
    border-radius: 36px;
  }
`;

const OfferCardContainer = styled.div<{ $isBuilder?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: ${({ $isBuilder }) =>
    $isBuilder
      ? `5px solid ${tokens.BRAND_MEDIUM_BLUE}`
      : `1px solid ${tokens.OFFWHITE_40}`};
  background-color: ${tokens.OFFWHITE_80};
  min-width: 320px;
  max-width: 320px;
`;

const BuilderOfferLabel = styled.div`
  padding: 8px 12px;
  background-color: ${tokens.BRAND_MEDIUM_BLUE};
  color: ${tokens.WHITE};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const CardFooter = styled.div`
  padding: 0 24px 24px;
  display: grid;
  grid-template-columns: 1fr auto auto;

  > :first-child {
    margin-right: 2rem;
  }
`;

const CustomButton = styled.button`
  font-size: 12px;
  padding: 12px 27px;
  border-radius: 28px;
  color: ${tokens.WHITE};
  border-color: ${tokens.BRAND_HERO_BLUE};
  background: ${tokens.BRAND_HERO_BLUE};
  cursor: pointer;

  &:hover {
    opacity: 0.6;
  }
`;

export { OfferCard, NewOfferCard };
